import moment from 'moment-timezone';

/** Period keys matching legacy Limitless dashboard select values (0–4). */
export const PERIOD = {
  TODAY: 0,
  THIS_WEEK: 1,
  THIS_MONTH: 2,
  LAST_3_MONTHS: 3,
  THIS_YEAR: 4,
};

/** Shared period filter for all dashlate-style tiles (matches old HTML options). */
export const PERIOD_OPTIONS = [
  { value: PERIOD.TODAY, label: 'Today' },
  { value: PERIOD.THIS_WEEK, label: 'This Week' },
  { value: PERIOD.THIS_MONTH, label: 'This Month' },
  { value: PERIOD.LAST_3_MONTHS, label: 'Last 3 Months' },
  { value: PERIOD.THIS_YEAR, label: 'This Year' },
];

export const DELIVERY_TYPE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'pickup', label: 'Pick Up' },
  { value: 'deliver', label: 'Deliver' },
  { value: 'completed', label: 'Completed' },
];

/**
 * Orders-by-period uses rolling windows (API sinceDays), not calendar periods.
 * Labels match old dashboard i18n (value "31d" = Last 30 Days in UI).
 */
export const ORDER_RANGE_OPTIONS = [
  { value: '1d', label: 'Today' },
  { value: '7d', label: 'Last 7 Days' },
  { value: '31d', label: 'Last 30 Days' },
  { value: '3m', label: 'Last 3 Months' },
  { value: '12m', label: 'Last 12 Months' },
];

/** Parse orders chart range like "7d" / "3m" into sinceDays + groupBy (parity with box-api.js). */
export function getOrderRangeMeta(selectedRange) {
  const raw = selectedRange || '7d';
  const barChartDuration = parseInt(raw.slice(0, -1), 10) || 7;
  const unit = raw.slice(-1);
  const barChartGroupBy = /m/i.test(unit) ? 'month' : 'day';
  const sinceDays =
    barChartGroupBy === 'day'
      ? barChartDuration
      : moment().diff(
          moment()
            .subtract(barChartDuration - 1, barChartGroupBy)
            .startOf('month'),
          'days'
        );
  return { barChartDuration, barChartGroupBy, sinceDays };
}

/**
 * Format for API to_timestamp(..., 'YYYY-MM-DD HH24:MI:SS').
 * Do not append a zone offset — the SQL mask ignores it and UTC EXTRACT(MONTH)
 * on pickup_date otherwise pulls an extra prior month into "Last 3 Months".
 */
function fmt(d) {
  return moment(d).format('YYYY-MM-DD HH:mm:ss');
}

/**
 * Exact date-range helpers from Limitless box-api.js (todayDates / currentWeek / …).
 * Keep quirks (e.g. week start math) so API payloads match the old dashboard.
 */
export function getPeriodDateRange(period) {
  const now = new Date();
  switch (Number(period)) {
    case PERIOD.TODAY: {
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const end = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        23,
        59,
        59,
        999
      );
      return [fmt(start), fmt(end)];
    }
    case PERIOD.THIS_WEEK: {
      const end = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() + (7 - now.getDay()),
        23,
        59,
        59,
        999
      );
      const start = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() - now.getDay() + 1,
        0,
        0,
        0,
        0
      );
      return [fmt(start), fmt(end)];
    }
    case PERIOD.THIS_MONTH: {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(
        now.getFullYear(),
        now.getMonth() + 1,
        0,
        23,
        59,
        59,
        999
      );
      return [fmt(start), fmt(end)];
    }
    case PERIOD.LAST_3_MONTHS: {
      // Exactly 3 calendar months inclusive of the current month
      // (e.g. Jul → May 1 … Jul 31), not month-3 which yields 4 bars.
      const end = moment().endOf('day');
      const start = moment().startOf('month').subtract(2, 'months').startOf('day');
      return [fmt(start.toDate()), fmt(end.toDate())];
    }
    case PERIOD.THIS_YEAR: {
      const start = new Date(now.getUTCFullYear(), 0, 1, 0, 0, 0, 0);
      const end = new Date(now.getUTCFullYear(), 12, 0, 0, 0, 0, 0);
      return [fmt(start), fmt(end)];
    }
    default:
      return getPeriodDateRange(PERIOD.THIS_WEEK);
  }
}

/**
 * Keep only the last `count` calendar months (inclusive of current).
 * Guards against API EXTRACT(MONTH FROM pickup_date) using UTC and leaking
 * an extra prior month into Last 3 Months charts.
 */
export function filterLastCalendarMonths(rows, count, getYearMonth) {
  if (!Array.isArray(rows) || !rows.length) return [];
  const allowed = new Set();
  for (let i = 0; i < count; i += 1) {
    const m = moment().startOf('month').subtract(i, 'months');
    allowed.add(`${m.year()}-${m.month() + 1}`);
  }
  return rows.filter((row) => {
    const ym = getYearMonth(row);
    if (!ym) return false;
    return allowed.has(`${Number(ym.year)}-${Number(ym.month)}`);
  });
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/** Normalize API date field the same way box-api.js does for day/week/month charts. */
export function formatApiDateLabel(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'string') {
    return value.split('-').join('/').split('T')[0];
  }
  const m = moment.utc(value);
  return m.isValid() ? m.format('YYYY/MM/DD') : String(value);
}

export function formatDashlateLabel(row, period) {
  const type = Number(period);
  if (type === 0 || type === 1 || type === 2) {
    return formatApiDateLabel(row.to_date);
  }
  if (type === 3) {
    const part = Number(row.date_part) || 0;
    const years =
      row.date_part_Year != null
        ? row.date_part_Year
        : row.date_part_year != null
          ? row.date_part_year
          : row.to_date
            ? new Date(row.to_date).getUTCFullYear()
            : new Date().getFullYear();
    return `${MONTHS[part - 1] || part} ${years}`;
  }
  if (type === 4) {
    return String(row.date_part != null ? row.date_part : '');
  }
  return formatApiDateLabel(row.to_date);
}

export function formatSalesLabel(row, period) {
  const type = Number(period);
  if (type === 0 || type === 1 || type === 2) {
    return formatApiDateLabel(row.date);
  }
  if (type === 3) {
    const part = Number(row.date) || 0;
    return `${MONTHS[part - 1] || part} ${row.date_year || ''}`.trim();
  }
  if (type === 4) {
    return String(row.date != null ? row.date : '');
  }
  return formatApiDateLabel(row.date);
}

/** Chronological (oldest → newest) — mirrors Apex categories.reverse() in box-api. */
export function reverseChronological(rows) {
  return Array.isArray(rows) ? [...rows].reverse() : [];
}

/** Transaction metrics use complete local calendar periods, including Sundays and Dec 31. */
export function getTransactionDateRange(period) {
  if (Number(period) === PERIOD.THIS_WEEK) return [fmt(moment().startOf('isoWeek')), fmt(moment().endOf('isoWeek'))];
  if (Number(period) === PERIOD.THIS_YEAR) return [fmt(moment().startOf('year')), fmt(moment().endOf('year'))];
  return getPeriodDateRange(period);
}



// WEBPACK FOOTER //
// ./src/components/dashboard2/dateRanges.js